import * as THREE from 'three'
import Scene      from './Scene'

const INTERACTIVE_OBJECTS_DISTANCE = 400

class Scene360Spheric extends Scene {

  constructor(props) {
    super(props)
    this.geometry = null
    this.material = null
    this.mesh = null
    this.video = null
  }

  destroy() {
    super.destroy()

    if (this.video) {
      this.video.pause()
      this.video.src = ''
      this.video.load()
      delete this.video
    }
  }

  beforeRenderSceneLoop() {
    this.initGeometry()
    this.initMaterial()
    this.initMesh()
  }

  init() {
    super.init()
    this.initInteractiveObjects()
  }

  initGeometry() {
    this.geometry = new THREE.SphereBufferGeometry(500, 60, 40)
    this.geometry.scale(-1, 1, 1)

    this.geometries.push(this.geometry)
  }

  initMaterial() {
    if (!this.config.texture) {
      throw new Error('Error initializing 360 panorama scene. No texture provided.')
    }

    let texture
    if (this.config.type === '360-image') {
      texture = new THREE.TextureLoader(this.loadingManager).load(this.config.texture)
      this.material = new THREE.MeshBasicMaterial({ map: texture })
    } else if (this.config.type === '360-video') {
      let video = document.createElement('video')
      video.crossOrigin = 'anonymous'
      video.width = 1920
      video.height = 960
      video.loop = true
      if (this.config.volume) {
        video.volume = this.config.volume
      } else {
        video.muted = true
      }

      video.src = this.config.texture
      video.setAttribute('webkit-playsinline', 'webkit-playsinline')
      video.play()

      video.addEventListener('canplay', () => {
        if (!this.config.interactiveObjects || this.config.interactiveObjects.length === 0) {
          this.triggerLoadedEvent()
        }
      })

      let playEvents = ['play', 'playing']
      playEvents.map((eventName) => {
        video.addEventListener(eventName, (event) => {
          this.viewportContainer.dispatchEvent(new CustomEvent(
            'sceneVideoPlay',
            { 'detail': { originalEvent: event } }
          ))
        })
      })

      let stopEvents = ['pause', 'stalled', 'suspend', 'waiting']
      stopEvents.map((eventName) => {
        video.addEventListener(eventName, (event) => {
          this.viewportContainer.dispatchEvent(new CustomEvent(
            'sceneVideoPause',
            { 'detail': { originalEvent: event } }
          ))
        })
      })

      this.video = video

      texture = new THREE.VideoTexture(video)
      texture.minFilter = THREE.LinearFilter
      texture.format = THREE.RGBFormat

      this.material = new THREE.MeshBasicMaterial({ map: texture })
      //this.scene.add(this.mesh)
    }

    this.materials.push(this.material)
    this.textures.push(texture)
  }

  initMesh() {
    if (!this.geometry) {
      throw new Error('Error 360 panorama scene mesh initialization. Geometry is need to be initialized first.')
    }

    if (!this.material) {
      throw new Error('Error 360 panorama scene mesh initialization. Material is need to be initialized first.')
    }

    this.mesh = new THREE.Mesh(this.geometry, this.material)
    this.mesh.rotation.y = Math.PI
    this.scene.add(this.mesh)
  }

  initInteractiveObjects() {
    if (this.config.interactiveObjects && this.config.interactiveObjects.length) {
      let spritesTexturesLoad = []

      this.config.interactiveObjects.map((interactiveObjectConfig, index) => {
        let spriteGroup = new THREE.Group()
        spriteGroup.name = 'hotspot' + index

        spriteGroup.position.copy(this.latLonToCoordinates(
          interactiveObjectConfig.position[0],
          interactiveObjectConfig.position[1],
          INTERACTIVE_OBJECTS_DISTANCE
        ))
        spriteGroup.userData.sceneId = this.config.id
        spriteGroup.userData.title = interactiveObjectConfig.title
        spriteGroup.userData.content = interactiveObjectConfig.content

        if (interactiveObjectConfig.styles) {
          spriteGroup.userData.styles = interactiveObjectConfig.styles
        }

        let materialConfig = {}

        materialConfig.map = this.defaultSpriteTexture()
        this.textures.push(materialConfig.map)

        if (interactiveObjectConfig.color) {
          materialConfig.color = interactiveObjectConfig.color
        }

        let spriteMaterial = new THREE.SpriteMaterial(materialConfig)

        if (interactiveObjectConfig.image) {
          spritesTexturesLoad.push(new Promise(resolve => {
            new THREE.TextureLoader(this.loadingManager).load(interactiveObjectConfig.image, (texture) => {
              spriteMaterial.map = texture
              this.textures.push(texture)
              resolve()
            })
          }))
        }

        let sprite = new THREE.Sprite(spriteMaterial)
        sprite.scale.set(interactiveObjectConfig.size, interactiveObjectConfig.size, 1)
        sprite.userData.interactiveParent = spriteGroup

        let glow = this.createGlowObject(interactiveObjectConfig)

        spriteGroup.add(sprite)
        spriteGroup.add(glow)

        this.interactiveObjects.push(sprite)
        this.scene.add(spriteGroup)
      })

      // workaround for sprites size bug
      Promise.all(spritesTexturesLoad).then(() => {
        let cubeCamera = new THREE.CubeCamera(1, 100000, 128)
        this.scene.add(cubeCamera)

        cubeCamera.position.copy(this.camera.position)
        cubeCamera.update(this.renderer, this.scene)
      })
    }
  }

  videoPlay() {
    if (this.video) {
      this.video.play()
    }
  }

  videoPause() {
    if (this.video) {
      this.video.pause()
    }
  }

  videoVolumeSet(volume) {
    if (this.video) {
      if (!volume) {
        this.video.muted = true
      } else {
        if (this.video.muted) {
          this.video.muted = false
        }

        this.video.volume = volume
      }
    }
  }

  defaultSpriteTexture() {
    if (typeof this._defaultSpriteTexture === 'undefined' || !this._defaultSpriteTexture) {
      let texture = new THREE.Texture()
      let canvas
      let context
      let size = 2

      canvas = document.createElement('canvas')
      context = canvas.getContext('2d')
      canvas.height = size
      canvas.width = size

      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, size, size)

      texture.image = canvas
      texture.needsUpdate = true

      this._defaultSpriteTexture = texture
    }

    return this._defaultSpriteTexture
  }

  createGlowObject(interactiveObjectConfig) {
    let texture = new THREE.TextureLoader().load('/scenes/helpers/hotspot-glow.png')
    // glow decoration
    let spriteMaterial = new THREE.SpriteMaterial({
      transparent: true,
      opacity: 0,
      color: '#ffffff',
      map: texture,
    })
    let sprite = new THREE.Sprite(spriteMaterial)
    sprite.name = 'glow'
    sprite.scale.setScalar(interactiveObjectConfig.size * 1.6)

    this.textures.push(texture)

    return sprite
  }

  latLonToCoordinates(lat, lon, radius) {
    let v = new THREE.Vector3()
    let phi = THREE.Math.degToRad(90 - THREE.Math.clamp(lat, -85, 85))
    let theta = THREE.Math.degToRad(lon)

    v.x = radius * Math.sin(phi) * Math.cos(theta)
    v.y = radius * Math.cos(phi)
    v.z = radius * Math.sin(phi) * Math.sin(theta)

    return v
  }
}

export default Scene360Spheric
